import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "leaflet-control-geocoder/dist/Control.Geocoder.css";
import "leaflet-control-geocoder";
import logo from "../assets/image/eblackdog_sentinal.png";

/* =========================================================
   GOOGLE MAPS LOADER
========================================================= */
const GOOGLE_MAPS_SCRIPT_ID = "eblackdog-google-maps-script";
const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim() || "";
const DEFAULT_CENTER = { lat: 20.5937, lng: 78.9629 };
const DEFAULT_ZOOM = 5;
const SELECTED_LOCATION_ZOOM = 16;

let googleMapsLoadingPromise = null;

function findExistingGoogleMapsScript() {
  const scriptById = document.getElementById(GOOGLE_MAPS_SCRIPT_ID);
  if (scriptById) return scriptById;
  return document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
}

function getGoogleMapsErrorMessage(error) {
  const errorText = String(error?.message || error?.name || error || "");

  if (errorText.includes("RefererNotAllowedMapError")) {
    return "This website is not authorized for the Google Maps API key. Add this domain under the API key website restrictions.";
  }
  if (errorText.includes("InvalidKeyMapError")) {
    return "The Google Maps API key is invalid. Check REACT_APP_GOOGLE_MAPS_API_KEY.";
  }
  if (errorText.includes("ApiNotActivatedMapError")) {
    return "Maps JavaScript API is not enabled for this Google Cloud project.";
  }
  if (errorText.includes("BillingNotEnabledMapError")) {
    return "Billing is not enabled for the Google Cloud project used by this API key.";
  }
  if (errorText.includes("MissingKeyMapError")) {
    return "Google Maps API key is missing.";
  }

  return error?.message || "Google Maps JavaScript API could not be loaded.";
}

function loadGoogleMapsScript(apiKey) {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.reject(new Error("Google Maps can only be loaded in a browser."));
  }

  if (window.google?.maps) return Promise.resolve(window.google.maps);

  if (!apiKey) {
    return Promise.reject(
      new Error("Google Maps API key is missing. Add REACT_APP_GOOGLE_MAPS_API_KEY to the .env file.")
    );
  }

  if (googleMapsLoadingPromise) return googleMapsLoadingPromise;

  googleMapsLoadingPromise = new Promise((resolve, reject) => {
    let settled = false;

    const resolveOnce = () => {
      if (settled || !window.google?.maps) return;
      settled = true;
      resolve(window.google.maps);
    };

    const rejectOnce = (error) => {
      if (settled) return;
      settled = true;
      googleMapsLoadingPromise = null;
      reject(error instanceof Error ? error : new Error("Google Maps JavaScript API failed to load."));
    };

    const handleScriptLoad = () => {
      if (window.google?.maps) {
        resolveOnce();
        return;
      }
      rejectOnce(new Error("Google Maps script loaded, but the Maps API is unavailable."));
    };

    const handleScriptError = () => {
      rejectOnce(new Error("Google Maps JavaScript API network request failed."));
    };

    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (typeof previousAuthFailure === "function") {
        try {
          previousAuthFailure();
        } catch (callbackError) {
          console.error("Previous Google Maps authentication callback failed:", callbackError);
        }
      }
      rejectOnce(
        new Error(
          "Google Maps authentication failed. Check the API key, enabled APIs, billing and website restrictions."
        )
      );
    };

    const existingScript = findExistingGoogleMapsScript();
    if (existingScript) {
      existingScript.addEventListener("load", handleScriptLoad, { once: true });
      existingScript.addEventListener("error", handleScriptError, { once: true });
      window.setTimeout(() => {
        if (window.google?.maps) resolveOnce();
      }, 0);
      return;
    }

    const script = document.createElement("script");
    script.id = GOOGLE_MAPS_SCRIPT_ID;

    const queryParameters = new URLSearchParams({
      key: apiKey,
      libraries: "places",
      v: "weekly",
    });

    script.src = `https://maps.googleapis.com/maps/api/js?${queryParameters.toString()}`;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-google-maps-loader", "eblackdog");
    script.onload = handleScriptLoad;
    script.onerror = () => {
      script.remove();
      handleScriptError();
    };

    document.head.appendChild(script);
  });

  return googleMapsLoadingPromise;
}

function normalizeMapProvider(value) {
  const provider = String(value || "").trim().toLowerCase();
  if (provider === "google" || provider === "googlemaps" || provider === "google_maps") {
    return "google";
  }
  return "openstreetmap";
}

/* ========================================================= */

const initialForm = {
  run_id: "",
  member_id: "",
  title: "",
  vehicle: "",
  start_time: "",
  end_time: "",
  from_name: "",
  from_lat: "",
  from_lng: "",
  to_name: "",
  to_lat: "",
  to_lng: "",
};

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

function valueOrEmpty(value) {
  return value === null || value === undefined ? "" : String(value);
}

function api(path) {
  const base = API_BASE_URL.replace(/\/$/, "");
  if (base.endsWith("/api") && path.startsWith("/api/")) {
    return `${base}${path.replace(/^\/api/, "")}`;
  }
  return `${base}${path}`;
}

function getToken() {
  return (
    localStorage.getItem("token") ||
    localStorage.getItem("authToken") ||
    localStorage.getItem("adminToken")
  );
}

function getClientId() {
  return (
    localStorage.getItem("adminId") ||
    localStorage.getItem("admin_id") ||
    localStorage.getItem("clientId") ||
    localStorage.getItem("client_id") ||
    null
  );
}

function toDatetimeLocal(value) {
  if (!value) return "";

  const raw = String(value).trim();
  if (!raw) return "";

  const normalized = raw.replace("T", " ").split(".")[0];
  const [datePart, timePart = "00:00:00"] = normalized.split(" ");

  const [yyyy, mm, dd] = datePart.split("-").map(Number);
  const [hh = 0, mi = 0] = timePart.split(":").map(Number);

  if (!yyyy || !mm || !dd) return "";

  const pad = (n) => String(n).padStart(2, "0");

  return `${yyyy}-${pad(mm)}-${pad(dd)}T${pad(hh)}:${pad(mi)}`;
}

/** Normalize common API shapes into a plain array */
function toGuardsArray(raw) {
  if (Array.isArray(raw)) return raw;
  if (Array.isArray(raw?.guards)) return raw.guards;
  if (Array.isArray(raw?.data)) return raw.data;
  if (Array.isArray(raw?.list)) return raw.list;
  if (Array.isArray(raw?.members)) return raw.members;
  return [];
}

function createCustomIcon({
  label,
  bg = "#2563eb",
  border = "#ffffff",
  textColor = "#ffffff",
  isLogo = false,
  logoUrl = "",
}) {
  if (isLogo && logoUrl) {
    return L.divIcon({
      className: "custom-map-pin-wrapper",
      html: `
        <div style="
          position: relative;
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: ${bg};
          border: 3px solid ${border};
          box-shadow: 0 4px 14px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        ">
          <img src="${logoUrl}" alt="logo" style="width: 28px; height: 28px; object-fit: contain;" />
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 40],
      popupAnchor: [0, -32],
    });
  }

  return L.divIcon({
    className: "custom-map-pin-wrapper",
    html: `
      <div style="
        position: relative;
        width: 30px;
        height: 30px;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        background: ${bg};
        border: 2px solid ${border};
        box-shadow: 0 4px 10px rgba(0,0,0,0.25);
        display: flex;
        align-items: center;
        justify-content: center;
      ">
        <span style="
          transform: rotate(45deg);
          font-size: 12px;
          font-weight: 700;
          color: ${textColor};
          font-family: Arial, sans-serif;
          line-height: 1;
        ">
          ${label}
        </span>
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -28],
  });
}

const fromIcon = createCustomIcon({ label: "F", bg: "#16a34a", isLogo: true, logoUrl: logo });
const toIcon = createCustomIcon({ label: "T", bg: "#dc2626", isLogo: true, logoUrl: logo });

function createGoogleMarkerIcon(color) {
  return {
    path: window.google.maps.SymbolPath.CIRCLE,
    scale: 12,
    fillColor: color,
    fillOpacity: 1,
    strokeColor: "#ffffff",
    strokeWeight: 2,
  };
}

export default function AddEscort() {
  const navigate = useNavigate();
  const location = useLocation();
  const skin = useSkin();

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const editId = params.get("edit_id") || "";
  const isEdit = Boolean(editId);

  const [form, setForm] = useState(initialForm);
  const [guards, setGuards] = useState([]);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [activeTarget, setActiveTarget] = useState(null);
  const activeTargetRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapError, setMapError] = useState("");

  /* Leaflet refs */
  const leafletMapElementRef = useRef(null);
  const leafletMapInstanceRef = useRef(null);
  const leafletGeocoderRef = useRef(null);
  const leafletFromMarkerRef = useRef(null);
  const leafletToMarkerRef = useRef(null);
  const leafletClickHandlerRef = useRef(null);

  /* Google Maps refs */
  const googleMapElementRef = useRef(null);
  const googleMapInstanceRef = useRef(null);
  const googleGeocoderRef = useRef(null);
  const googleFromMarkerRef = useRef(null);
  const googleToMarkerRef = useRef(null);
  const googleClickListenerRef = useRef(null);

  const didFitBoundsRef = useRef(false);

  const mapProvider = useMemo(
    () => normalizeMapProvider(dashboardData.user_map),
    [dashboardData.user_map]
  );
  const isGoogleMap = mapProvider === "google";

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google" ? "/route-master" : "/route-master1",
    [dashboardData.user_map]
  );

  const setTarget = (target) => {
    setActiveTarget(target);
    activeTargetRef.current = target;
    toast(`Click on the map or use search to set: ${target.toUpperCase()} location`, {
      icon: "📍",
      duration: 3000,
    });
  };

  const setLatLngOnTarget = useCallback(
    (target, lat, lng, shouldCenter = false, label = "") => {
      if (!target) return;

      const latNum = Number(lat);
      const lngNum = Number(lng);

      if (!Number.isFinite(latNum) || !Number.isFinite(lngNum)) return;

      const lat7 = latNum.toFixed(7);
      const lng7 = lngNum.toFixed(7);

      if (target === "from") {
        setForm((prev) => ({
          ...prev,
          from_lat: lat7,
          from_lng: lng7,
          from_name: label || prev.from_name,
        }));
      }

      if (target === "to") {
        setForm((prev) => ({
          ...prev,
          to_lat: lat7,
          to_lng: lng7,
          to_name: label || prev.to_name,
        }));
      }

      if (shouldCenter) {
        if (!isGoogleMap && leafletMapInstanceRef.current) {
          leafletMapInstanceRef.current.invalidateSize(true);
          leafletMapInstanceRef.current.setView([latNum, lngNum], SELECTED_LOCATION_ZOOM);
        }

        if (isGoogleMap && googleMapInstanceRef.current) {
          googleMapInstanceRef.current.panTo({ lat: latNum, lng: lngNum });
          googleMapInstanceRef.current.setZoom(SELECTED_LOCATION_ZOOM);
        }
      }
    },
    [isGoogleMap]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  /* =======================================================
     DESTROY HELPERS
  ======================================================= */
  const destroyLeafletMap = useCallback(() => {
    const map = leafletMapInstanceRef.current;

    if (map && leafletClickHandlerRef.current) {
      map.off("click", leafletClickHandlerRef.current);
    }

    if (map) map.remove();

    leafletMapInstanceRef.current = null;
    leafletGeocoderRef.current = null;
    leafletFromMarkerRef.current = null;
    leafletToMarkerRef.current = null;
    leafletClickHandlerRef.current = null;
    didFitBoundsRef.current = false;
  }, []);

  const destroyGoogleMap = useCallback(() => {
    if (googleClickListenerRef.current) {
      googleClickListenerRef.current.remove();
      googleClickListenerRef.current = null;
    }

    if (googleFromMarkerRef.current) {
      googleFromMarkerRef.current.setMap(null);
      googleFromMarkerRef.current = null;
    }

    if (googleToMarkerRef.current) {
      googleToMarkerRef.current.setMap(null);
      googleToMarkerRef.current = null;
    }

    googleMapInstanceRef.current = null;
    googleGeocoderRef.current = null;
    didFitBoundsRef.current = false;
  }, []);

  /* =======================================================
     FETCH PAGE DATA
  ======================================================= */
  const fetchPageData = async (token) => {
    try {
      setLoading(true);

      const dashRes = await fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (dashRes.ok) {
        const dashJson = await dashRes.json();
        const companyDetails = dashJson?.data?.companyDetails || {};
        setDashboardData(companyDetails);
      }

      // GET /api/escort/guards  →  { data: { guards: [...] } }
      const guardsRes = await fetch(api("/api/escort/guards"), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const guardsJson = await guardsRes.json();
      if (guardsRes.ok && guardsJson.success !== false) {
        setGuards(toGuardsArray(guardsJson.data));
      } else {
        setGuards([]);
      }

      // GET /api/escort/runs/:id
      if (isEdit && editId) {
        const runRes = await fetch(api(`/api/escort/runs/${editId}`), {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        const runJson = await runRes.json();
        const run = runJson?.data?.run || runJson?.data || null;

        if (run) {
          setForm({
            run_id: valueOrEmpty(run.run_id),
            member_id: valueOrEmpty(run.member_id),
            title: valueOrEmpty(run.title),
            vehicle: valueOrEmpty(run.vehicle),
            start_time: toDatetimeLocal(run.start_time),
            end_time: toDatetimeLocal(run.end_time),
            from_name: valueOrEmpty(run.from_name),
            from_lat: valueOrEmpty(run.from_lat ?? run.start_lat),
            from_lng: valueOrEmpty(run.from_lng ?? run.start_lng),
            to_name: valueOrEmpty(run.to_name),
            to_lat: valueOrEmpty(run.to_lat ?? run.end_lat),
            to_lng: valueOrEmpty(run.to_lng ?? run.end_lng),
          });
        }
      }
    } catch (err) {
      console.error("Page data fetch error:", err);
      toast.error(err.message || "Failed to load page data");
      setGuards([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    fetchPageData(token);
  }, [navigate, editId]);

  /* =======================================================
     GEOCODE
  ======================================================= */
  const geocodeAddress = async (target, address) => {
    if (!address || !String(address).trim()) {
      toast.error("Please enter a location name first");
      return;
    }

    try {
      if (isGoogleMap && window.google?.maps) {
        const geocoder = googleGeocoderRef.current || new window.google.maps.Geocoder();
        googleGeocoderRef.current = geocoder;

        geocoder.geocode({ address }, (results, status) => {
          if (status === "OK" && results?.[0]) {
            const loc = results[0].geometry.location;
            const label = results[0].formatted_address || address;
            setLatLngOnTarget(target, loc.lat(), loc.lng(), true, label);
            toast.success(`Location found: ${label}`);
          } else {
            toast.error("Location not found");
          }
        });
        return;
      }

      // OpenStreetMap Nominatim
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
          address
        )}&limit=1`,
        {
          headers: {
            Accept: "application/json",
          },
        }
      );
      const data = await res.json();

      if (data?.[0]) {
        const lat = parseFloat(data[0].lat);
        const lng = parseFloat(data[0].lon);
        const label = data[0].display_name || address;
        setLatLngOnTarget(target, lat, lng, true, label);
        toast.success(`Location found: ${label}`);
      } else {
        toast.error("Location not found");
      }
    } catch (err) {
      console.error("Geocode error:", err);
      toast.error("Failed to search location");
    }
  };

  /* =======================================================
     SUBMIT
  ======================================================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.member_id) {
      toast.error("Please select a guard");
      return;
    }
    if (!form.title) {
      toast.error("Please enter a title");
      return;
    }
    if (!form.start_time || !form.end_time) {
      toast.error("Please set start and end time");
      return;
    }
    if (!form.from_lat || !form.from_lng) {
      toast.error("Please set From location on the map");
      return;
    }
    if (!form.to_lat || !form.to_lng) {
      toast.error("Please set To location on the map");
      return;
    }

    try {
      setSaving(true);

      const token = getToken();
      const clientId = getClientId();

      const payload = {
        member_id: form.member_id,
        title: form.title,
        vehicle: form.vehicle || "",
        start_time: form.start_time
          ? form.start_time.replace("T", " ") + ":00"
          : null,
        end_time: form.end_time
          ? form.end_time.replace("T", " ") + ":00"
          : null,
        from_name: form.from_name || "",
        from_lat: form.from_lat,
        from_lng: form.from_lng,
        to_name: form.to_name || "",
        to_lat: form.to_lat,
        to_lng: form.to_lng,
        client_id: clientId, // sent, but backend currently ignores it (uses getSingletonClientId)
      };

      // Correct paths matching backend routes
      const url = isEdit
        ? api(`/api/escort/runs/${editId}`)
        : api("/api/escort/runs");

      const res = await fetch(url, {
        method: isEdit ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (!res.ok || json.success === false) {
        throw new Error(json.message || "Failed to save escort");
      }

      toast.success(
        isEdit ? "Escort updated successfully!" : "Escort created successfully!"
      );
      navigate("/escort_list");
    } catch (err) {
      console.error("Escort save error:", err);
      toast.error(err.message || "Failed to save escort");
    } finally {
      setSaving(false);
    }
  };

  /* =======================================================
     SYNC MARKERS
  ======================================================= */
  const syncMarkers = useCallback(() => {
    const fLat = parseFloat(form.from_lat || "");
    const fLng = parseFloat(form.from_lng || "");
    const tLat = parseFloat(form.to_lat || "");
    const tLng = parseFloat(form.to_lng || "");

    if (!isGoogleMap) {
      const map = leafletMapInstanceRef.current;
      if (!map) return;

      map.invalidateSize(true);

      if (Number.isFinite(fLat) && Number.isFinite(fLng)) {
        if (leafletFromMarkerRef.current) map.removeLayer(leafletFromMarkerRef.current);
        leafletFromMarkerRef.current = L.marker([fLat, fLng], { icon: fromIcon })
          .addTo(map)
          .bindPopup(`From: ${form.from_name || "Selected location"}`);
      } else if (leafletFromMarkerRef.current) {
        map.removeLayer(leafletFromMarkerRef.current);
        leafletFromMarkerRef.current = null;
      }

      if (Number.isFinite(tLat) && Number.isFinite(tLng)) {
        if (leafletToMarkerRef.current) map.removeLayer(leafletToMarkerRef.current);
        leafletToMarkerRef.current = L.marker([tLat, tLng], { icon: toIcon })
          .addTo(map)
          .bindPopup(`To: ${form.to_name || "Selected location"}`);
      } else if (leafletToMarkerRef.current) {
        map.removeLayer(leafletToMarkerRef.current);
        leafletToMarkerRef.current = null;
      }

      const layers = [leafletFromMarkerRef.current, leafletToMarkerRef.current].filter(Boolean);

      if (layers.length > 0) {
        const group = L.featureGroup(layers);
        map.fitBounds(group.getBounds().pad(0.25));
        didFitBoundsRef.current = true;
      } else if (!didFitBoundsRef.current) {
        map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);
      }

      return;
    }

    const map = googleMapInstanceRef.current;
    if (!map || !window.google?.maps) return;

    if (Number.isFinite(fLat) && Number.isFinite(fLng)) {
      const position = { lat: fLat, lng: fLng };
      if (googleFromMarkerRef.current) {
        googleFromMarkerRef.current.setPosition(position);
      } else {
        googleFromMarkerRef.current = new window.google.maps.Marker({
          map,
          position,
          icon: createGoogleMarkerIcon("#16a34a"),
          label: { text: "F", color: "#ffffff", fontWeight: "700", fontSize: "11px" },
        });
      }
      googleFromMarkerRef.current.setTitle(`From: ${form.from_name || "Selected location"}`);
    } else if (googleFromMarkerRef.current) {
      googleFromMarkerRef.current.setMap(null);
      googleFromMarkerRef.current = null;
    }

    if (Number.isFinite(tLat) && Number.isFinite(tLng)) {
      const position = { lat: tLat, lng: tLng };
      if (googleToMarkerRef.current) {
        googleToMarkerRef.current.setPosition(position);
      } else {
        googleToMarkerRef.current = new window.google.maps.Marker({
          map,
          position,
          icon: createGoogleMarkerIcon("#dc2626"),
          label: { text: "T", color: "#ffffff", fontWeight: "700", fontSize: "11px" },
        });
      }
      googleToMarkerRef.current.setTitle(`To: ${form.to_name || "Selected location"}`);
    } else if (googleToMarkerRef.current) {
      googleToMarkerRef.current.setMap(null);
      googleToMarkerRef.current = null;
    }

    const positions = [];
    if (googleFromMarkerRef.current) positions.push(googleFromMarkerRef.current.getPosition());
    if (googleToMarkerRef.current) positions.push(googleToMarkerRef.current.getPosition());

    if (positions.length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      positions.forEach((pos) => bounds.extend(pos));
      map.fitBounds(bounds, 60);
      didFitBoundsRef.current = true;
    } else if (!didFitBoundsRef.current) {
      map.setCenter(DEFAULT_CENTER);
      map.setZoom(DEFAULT_ZOOM);
    }
  }, [
    form.from_lat,
    form.from_lng,
    form.to_lat,
    form.to_lng,
    form.from_name,
    form.to_name,
    isGoogleMap,
  ]);

  useEffect(() => {
    if (loading) return;
    syncMarkers();
  }, [loading, syncMarkers]);

  /* =======================================================
     INIT: OPENSTREETMAP (LEAFLET)
  ======================================================= */
  useEffect(() => {
    if (loading || isGoogleMap) return undefined;
    if (!leafletMapElementRef.current || leafletMapInstanceRef.current) return undefined;

    setMapError("");
    destroyGoogleMap();

    try {
      delete L.Icon.Default.prototype._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
        iconUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
        shadowUrl:
          "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
      });

      const map = L.map(leafletMapElementRef.current, {
        zoomControl: true,
        attributionControl: true,
      });

      leafletMapInstanceRef.current = map;
      map.setView([DEFAULT_CENTER.lat, DEFAULT_CENTER.lng], DEFAULT_ZOOM);

      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", {
        maxZoom: 19,
        attribution: "&copy; OpenStreetMap contributors",
      }).addTo(map);

      const geocoder = L.Control.geocoder({
        defaultMarkGeocode: false,
        position: "topleft",
        placeholder: "Search location...",
        errorMessage: "Location not found",
      })
        .on("markgeocode", (e) => {
          const target = activeTargetRef.current;

          if (!target) {
            toast.error("Please click Pick on Map for From or To first.");
            return;
          }

          const center = e.geocode.center;

          if (e.geocode.bbox) {
            map.fitBounds(e.geocode.bbox);
          } else {
            map.setView(center, SELECTED_LOCATION_ZOOM);
          }

          setLatLngOnTarget(target, center.lat, center.lng, true, e.geocode.name || "");
          toast.success(`Location "${e.geocode.name}" found and pinned!`);
        })
        .addTo(map);

      leafletGeocoderRef.current = geocoder;

      const handleMapClick = (ev) => {
        const target = activeTargetRef.current;

        if (!target) {
          toast.error("Please click Pick on Map for From or To first.");
          return;
        }

        setLatLngOnTarget(target, ev.latlng.lat, ev.latlng.lng, true);
      };

      map.on("click", handleMapClick);
      leafletClickHandlerRef.current = handleMapClick;

      const resizeMap = () => {
        if (leafletMapInstanceRef.current) {
          leafletMapInstanceRef.current.invalidateSize(true);
        }
      };

      const timers = [
        window.setTimeout(resizeMap, 100),
        window.setTimeout(resizeMap, 500),
        window.setTimeout(resizeMap, 1000),
        window.setTimeout(resizeMap, 1800),
      ];

      window.addEventListener("resize", resizeMap);

      return () => {
        timers.forEach((t) => window.clearTimeout(t));
        window.removeEventListener("resize", resizeMap);
        destroyLeafletMap();
      };
    } catch (error) {
      console.error("OpenStreetMap initialization error:", error);
      setMapError(error.message || "Unable to initialize OpenStreetMap.");
      return undefined;
    }
  }, [loading, isGoogleMap, setLatLngOnTarget, destroyGoogleMap, destroyLeafletMap]);

  /* =======================================================
     INIT: GOOGLE MAPS
  ======================================================= */
  useEffect(() => {
    if (loading || !isGoogleMap) return undefined;
    if (!googleMapElementRef.current || googleMapInstanceRef.current) return undefined;

    let cancelled = false;
    setMapError("");
    setMapLoading(true);
    destroyLeafletMap();

    const initializeGoogleMap = async () => {
      try {
        await loadGoogleMapsScript(GOOGLE_MAPS_API_KEY);
        if (cancelled || !googleMapElementRef.current) return;

        const map = new window.google.maps.Map(googleMapElementRef.current, {
          center: DEFAULT_CENTER,
          zoom: DEFAULT_ZOOM,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
        });

        googleMapInstanceRef.current = map;
        googleGeocoderRef.current = new window.google.maps.Geocoder();

        googleClickListenerRef.current = map.addListener("click", (ev) => {
          const target = activeTargetRef.current;

          if (!target) {
            toast.error("Please click Pick on Map for From or To first.");
            return;
          }

          if (!ev.latLng) return;
          setLatLngOnTarget(target, ev.latLng.lat(), ev.latLng.lng(), true);
        });

        setMapLoading(false);
        syncMarkers();
      } catch (error) {
        console.error("Google Maps initialization error:", error);
        if (!cancelled) {
          setMapLoading(false);
          setMapError(getGoogleMapsErrorMessage(error));
        }
      }
    };

    initializeGoogleMap();

    return () => {
      cancelled = true;
      destroyGoogleMap();
    };
  }, [
    loading,
    isGoogleMap,
    setLatLngOnTarget,
    destroyLeafletMap,
    destroyGoogleMap,
    syncMarkers,
  ]);

  const guardsList = Array.isArray(guards) ? guards : [];

  if (loading) {
    return (
      <Layout
        dashboardData={dashboardData}
        routePage={routePage}
        activePage="escort"
        activeMenu="escort"
      >
        <div className="flex items-center justify-center py-20 gap-3">
          <div className="w-8 h-8 border-3 border-[var(--accent-faint)] border-t-[var(--accent)] rounded-full animate-spin" />
          <span className="text-[var(--text-muted)] text-[13px]">Loading...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="escort"
      activeMenu="escort"
    >
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 5000,
          style: {
            background: "var(--card-bg)",
            color: "var(--text-primary)",
            border: "1px solid var(--card-border)",
            borderRadius: "12px",
            padding: "16px 20px",
            fontSize: "13px",
            fontWeight: "600",
            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
          },
          success: { iconTheme: { primary: "#22c55e", secondary: "white" } },
          error: { iconTheme: { primary: "#ef4444", secondary: "white" } },
        }}
      />

      <div className="font-['Inter',system-ui,sans-serif] text-[var(--text-primary)]">
        <h1 className="text-[22px] font-extrabold text-[var(--text-primary)] tracking-tight m-0">
          Escort Run
        </h1>
        <p className="text-[13px] text-[var(--text-muted)] m-0 mb-[22px]">
          {isEdit ? "Edit an existing escort run" : "Create a new escort run"}
        </p>

        {/* Top Card */}
        <div className="relative overflow-hidden bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="absolute top-0 left-0 right-0 h-[3px] bg-[var(--accent)]" />
          <div className="flex items-center justify-between gap-3.5 flex-wrap">
            <div>
              <h2 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                {isEdit ? "Edit Escort" : "New Escort"}
              </h2>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Manage guard, title, vehicle, planned time, and route points
              </p>
            </div>
            <div className="flex gap-3 flex-wrap">
              <button
                type="button"
                onClick={() => navigate(routePage)}
                className="border-0 rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-95 hover:-translate-y-0.5"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                Normal Route
              </button>
              <button
                type="button"
                onClick={() => navigate("/escort_list")}
                className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2.5 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5"
              >
                Escort List
              </button>
            </div>
          </div>
        </div>

        {/* Form Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)] mb-[18px]">
          <div className="mb-3.5">
            <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
              {isEdit ? "Edit Escort Run" : "New Escort Run"}
            </h3>
            <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
              Create escort run and pick from/to coordinates directly from the map
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Guard <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <select
                  name="member_id"
                  value={form.member_id}
                  onChange={handleChange}
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                >
                  <option value="">— Select Guard —</option>
                  {guardsList.map((guard) => {
                    const memberId =
                      guard.member_id ||
                      guard.fld_memberId ||
                      guard.fld_memberld ||
                      guard.memberId;
                    const label =
                      guard.label || guard.name || guard.fld_name || guard.fld_username || memberId;

                    return (
                      <option key={memberId} value={memberId}>
                        {label} ({memberId})
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Title <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  placeholder="e.g., Coimbatore → Erode"
                  required
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Vehicle No.
                </label>
                <input
                  type="text"
                  name="vehicle"
                  value={form.vehicle}
                  onChange={handleChange}
                  placeholder="e.g., TN 66 AB 1234"
                  className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                />
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  Planned Start / End <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <input
                    type="datetime-local"
                    name="start_time"
                    value={form.start_time}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                  <input
                    type="datetime-local"
                    name="end_time"
                    value={form.end_time}
                    onChange={handleChange}
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)]"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  From <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="from_name"
                    value={form.from_name}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("from", form.from_name);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.from_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.from_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setTarget("from")}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{ background: "var(--btn-bg)", color: "var(--btn-text)" }}
                    >
                      Pick on Map
                    </button>
                    <button
                      type="button"
                      onClick={() => geocodeAddress("from", form.from_name)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wide text-[var(--text-sub)] mb-1.5">
                  To <span className="text-[var(--red)] ml-0.5">*</span>
                </label>
                <div className="space-y-3">
                  <input
                    type="text"
                    name="to_name"
                    value={form.to_name}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        geocodeAddress("to", form.to_name);
                      }
                    }}
                    placeholder="Search or click on map"
                    required
                    className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] transition-colors duration-150 focus:border-[var(--accent)] focus:shadow-[0_0_0_3px_var(--accent-glow)] placeholder:text-[var(--text-faint)]"
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <input
                      type="text"
                      value={form.to_lat}
                      readOnly
                      placeholder="Lat"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                    <input
                      type="text"
                      value={form.to_lng}
                      readOnly
                      placeholder="Lng"
                      className="w-full rounded-xl border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] outline-none px-3.5 py-3 text-[13px] opacity-70 cursor-default"
                    />
                  </div>

                  <div className="flex gap-3 flex-wrap">
                    <button
                      type="button"
                      onClick={() => setTarget("to")}
                      className="border-0 rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:opacity-90"
                      style={{ background: "var(--btn-bg)", color: "var(--btn-text)" }}
                    >
                      Pick on Map
                    </button>
                    <button
                      type="button"
                      onClick={() => geocodeAddress("to", form.to_name)}
                      className="border border-[var(--surface-border)] bg-[var(--surface)] text-[var(--text-primary)] rounded-xl px-4 py-2 text-[12px] font-extrabold cursor-pointer transition-colors duration-150 hover:bg-[var(--accent-faint)] hover:border-[var(--accent-border)]"
                    >
                      Search Location
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-5">
              <button
                type="submit"
                disabled={saving}
                className="w-full border-0 rounded-xl px-5 py-3 text-[13px] font-extrabold cursor-pointer transition-transform duration-150 hover:opacity-90 hover:-translate-y-0.5 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                style={{
                  background: "var(--btn-bg)",
                  color: "var(--btn-text)",
                  boxShadow: "0 4px 18px var(--accent-glow)",
                }}
              >
                {saving ? "Saving..." : isEdit ? "Update Escort Run" : "Save Escort Run"}
              </button>
            </div>
          </form>
        </div>

        {/* Map Section */}
        <div className="bg-[var(--card-bg)] border border-[var(--card-border)] rounded-2xl px-7 py-6 shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="mb-3.5 flex items-center justify-between gap-3 flex-wrap">
            <div>
              <h3 className="text-[18px] font-extrabold text-[var(--text-primary)] m-0">
                Escort Map
              </h3>
              <p className="text-[13px] text-[var(--text-muted)] m-0 mt-1">
                Active picker: {activeTarget ? activeTarget.toUpperCase() : "None"}
              </p>
            </div>

            <div className="inline-flex items-center gap-2 rounded-full border border-[var(--surface-border)] bg-[var(--surface)] px-3 py-2">
              <span
                className={`h-2.5 w-2.5 rounded-full ${
                  mapError
                    ? "bg-red-500"
                    : mapLoading
                    ? "animate-pulse bg-amber-500"
                    : "bg-emerald-500"
                }`}
              />
              <span className="text-[12px] font-extrabold text-[var(--text-primary)]">
                {mapError
                  ? "Map Error"
                  : mapLoading
                  ? "Loading Map"
                  : isGoogleMap
                  ? "Google Maps"
                  : "OpenStreetMap"}
              </span>
            </div>
          </div>

          <div className="w-full h-[520px] min-h-[520px] relative overflow-hidden rounded-2xl bg-[var(--surface)] border border-[var(--surface-border)]">
            {!isGoogleMap && (
              <div
                ref={leafletMapElementRef}
                id="escort-leaflet-map"
                className="w-full h-[520px] min-h-[520px] block relative z-[1] rounded-2xl overflow-hidden bg-[var(--surface)]"
              />
            )}

            {isGoogleMap && (
              <div
                ref={googleMapElementRef}
                id="escort-google-map"
                className="w-full h-[520px] min-h-[520px] block relative z-[1] rounded-2xl overflow-hidden bg-[var(--surface)]"
              />
            )}

            {mapLoading && (
              <div className="absolute inset-0 z-[1000] flex flex-col items-center justify-center gap-3 bg-[var(--card-bg)]/90 backdrop-blur-sm">
                <div className="h-9 w-9 animate-spin rounded-full border-4 border-[var(--accent-faint)] border-t-[var(--accent)]" />
                <p className="text-sm font-extrabold text-[var(--text-primary)]">
                  Loading Google Maps...
                </p>
              </div>
            )}

            {!mapLoading && mapError && (
              <div className="absolute inset-0 z-[1000] flex items-center justify-center bg-[var(--surface)] p-5">
                <div className="w-full max-w-md rounded-2xl border border-red-300 bg-white p-5 text-center shadow-xl">
                  <p className="text-sm font-extrabold text-red-700">Map could not be loaded</p>
                  <p className="mt-2 text-xs font-semibold text-red-600">{mapError}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        #escort-leaflet-map .leaflet-container,
        .leaflet-container {
          width: 100% !important;
          height: 520px !important;
          min-height: 520px !important;
          display: block !important;
          background: var(--surface) !important;
          z-index: 1 !important;
        }

        .leaflet-pane { z-index: 2 !important; }
        .leaflet-top, .leaflet-bottom { z-index: 10 !important; }
        .leaflet-control-container { z-index: 20 !important; }

        .custom-map-pin-wrapper {
          background: transparent !important;
          border: none !important;
        }

        .leaflet-control-geocoder {
          border-radius: 12px !important;
          border: 1px solid var(--surface-border) !important;
          background: var(--card-bg) !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15) !important;
          display: flex !important;
          align-items: center !important;
          padding: 4px !important;
          margin: 10px !important;
        }

        .leaflet-control-geocoder-form {
          display: flex !important;
          align-items: center !important;
        }

        .leaflet-control-geocoder input {
          background: var(--surface) !important;
          color: var(--text-primary) !important;
          padding: 8px 12px !important;
          border-radius: 8px !important;
          border: none !important;
          outline: none !important;
          font-size: 13px !important;
          font-family: 'Inter', system-ui, sans-serif !important;
          min-width: 200px !important;
        }

        .leaflet-control-geocoder input::placeholder {
          color: var(--text-faint) !important;
        }

        .leaflet-control-geocoder .leaflet-control-geocoder-icon {
          background: var(--btn-bg) !important;
          border-radius: 8px !important;
          width: 32px !important;
          height: 32px !important;
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
          cursor: pointer !important;
          margin-left: 4px !important;
          border: none !important;
        }

        .leaflet-control-geocoder .leaflet-control-geocoder-icon:before {
          content: "🔍" !important;
          font-size: 14px !important;
          line-height: 1 !important;
        }

        .leaflet-popup-content-wrapper {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-radius: 12px !important;
          border: 1px solid var(--card-border) !important;
        }

        .leaflet-popup-tip { background: var(--card-bg) !important; }

        .leaflet-popup-content {
          color: var(--text-primary) !important;
          font-family: 'Inter', system-ui, sans-serif !important;
          font-size: 13px !important;
          font-weight: 600 !important;
        }

        .leaflet-control-zoom { margin: 10px !important; }

        .leaflet-control-zoom a {
          background: var(--card-bg) !important;
          color: var(--text-primary) !important;
          border-color: var(--surface-border) !important;
        }

        .leaflet-control-zoom a:hover {
          background: var(--accent-faint) !important;
        }
      `}</style>
    </Layout>
  );
}